import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Lightbulb, Sparkles, Lock, ArrowRight, Search } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * /dashboard/ideas/browse — curated research ideas inside the client
 * dashboard. Shows Free / Premium badges; premium ideas are locked behind a
 * paywall CTA for users without a premium subscription.
 */
export default function BrowseIdeasInDashboard() {
  const { user } = useAuth() || {};
  const isPremium = (user?.plan_tier || "").toLowerCase() === "premium" || user?.has_active_premium === true;
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/research-ideas", { params: { limit: 60 } })
      .then((r) => setIdeas(r.data || []))
      .catch(() => setIdeas([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = ideas.filter((i) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (i.title + " " + (i.overview || "") + " " + (i.tags || []).join(" "))
      .toLowerCase().includes(s);
  });

  return (
    <div data-testid="browse-ideas-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-purple">CURATED RESEARCH IDEAS</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">
            Pick a starting point for your <em className="italic text-kb-purple">next paper</em>
          </h1>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">
            Mentor-curated ideas across domains. Free ideas are visible to everyone; premium ideas
            unlock with a Premium plan.
          </p>
        </div>
        <Link to="/dashboard/ideas/new" className="kb-btn-outline text-sm" data-testid="browse-submit-own-btn">
          Submit my own idea
        </Link>
      </div>

      <div className="mt-6 flex items-center gap-3 max-w-md">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-kb-ink-light" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search ideas, tags, domain…"
            className="kb-input pl-9"
            data-testid="browse-ideas-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="mt-10 text-center py-20 border border-dashed border-kb-divider rounded-sm bg-white">
          <Lightbulb className="w-7 h-7 text-kb-ink-light mx-auto" strokeWidth={1.5} />
          <h3 className="font-serif text-xl text-kb-ink mt-4">No curated ideas yet</h3>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-md mx-auto">
            Our mentors publish new ideas every week. Check back soon or submit your own.
          </p>
        </div>
      ) : (
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger-in" data-testid="browse-ideas-grid">
          {filtered.map((idea) => {
            const isPremiumIdea = !!idea.is_premium;
            const locked = isPremiumIdea && !isPremium;
            return (
              <div
                key={idea.id}
                className={`kb-card p-5 flex flex-col relative ${locked ? "opacity-95" : ""}`}
                data-testid={`browse-idea-${idea.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <Lightbulb className="w-5 h-5 text-kb-purple shrink-0" strokeWidth={1.6} />
                  <div className="flex gap-1">
                    {isPremiumIdea ? (
                      <span className="kb-mono text-[9px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> PREMIUM
                      </span>
                    ) : (
                      <span className="kb-mono text-[9px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm">FREE</span>
                    )}
                    {idea.difficulty && (
                      <span className="kb-mono text-[9px] uppercase tracking-wider text-kb-purple bg-kb-subtle border border-kb-divider px-1.5 py-0.5 rounded-sm">{idea.difficulty}</span>
                    )}
                  </div>
                </div>
                <h3 className="font-serif text-lg text-kb-ink mt-3 leading-snug line-clamp-2">
                  {idea.title}
                </h3>
                <p className={`text-xs text-kb-ink-muted mt-2 line-clamp-3 flex-1 ${locked ? "blur-[2px] select-none" : ""}`}>
                  {idea.overview}
                </p>
                {idea.tags?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {idea.tags.slice(0, 3).map((t) => (
                      <span key={t} className="kb-mono text-[9px] uppercase tracking-wider text-kb-ink-muted bg-kb-subtle px-1.5 py-0.5 rounded-sm">{t}</span>
                    ))}
                  </div>
                )}
                {locked ? (
                  <Link
                    to="/pricing"
                    className="mt-4 kb-btn-primary text-xs justify-center"
                    data-testid={`browse-idea-${idea.id}-unlock`}
                  >
                    <Lock className="w-3 h-3" /> Unlock with Premium
                  </Link>
                ) : (
                  <Link
                    to={`/research-ideas/${idea.id}`}
                    className="mt-4 inline-flex items-center justify-between border-t border-kb-divider pt-3 text-[11px] text-kb-ink-muted hover:text-kb-purple"
                    data-testid={`browse-idea-${idea.id}-open`}
                  >
                    <span className="kb-mono uppercase tracking-wider">View details</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
